import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Shield, LogOut, FileText, AlertTriangle, Search, Lock, Key,
  CheckSquare, X, Users, ClipboardCheck, Archive, BarChart3, Download
} from "lucide-react";
import { safeJsonRequest } from "../utils/safeFetch";
import ReferralCard, { STATUS_OPTIONS } from "../components/admin/ReferralCard";
import AuditLogPanel from "../components/admin/AuditLogPanel";
import AnalyticsBar from "../components/admin/AnalyticsBar";
import UserManagementPanel from "../components/admin/UserManagementPanel";
import ChangePasswordModal from "../components/admin/ChangePasswordModal";

/**
 * AdminDashboard — Secure admin portal orchestrator.
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("referrals");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [referralView, setReferralView] = useState("active");
  const [intakes, setIntakes] = useState([]);
  const [intakesLoading, setIntakesLoading] = useState(false);
  const [intakeFilter, setIntakeFilter] = useState("all");
  const [preferenceFilter, setPreferenceFilter] = useState("");
  const [exportingIntakes, setExportingIntakes] = useState(false);
  const [showDeclinedConsents, setShowDeclinedConsents] = useState(false);
  const [declinedConsents, setDeclinedConsents] = useState([]);
  const [declinedConsentsLoading, setDeclinedConsentsLoading] = useState(false);
  const [exportingDeclinedConsents, setExportingDeclinedConsents] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const token = sessionStorage.getItem("admin_token");
  const adminEmail = sessionStorage.getItem("admin_email");
  const adminName = sessionStorage.getItem("admin_name") || "Admin";
  const adminRole = sessionStorage.getItem("admin_role") || "admin";

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    loadReferrals();
  }, []); // mount-only

  useEffect(() => {
    const interval = setInterval(() => {
      safeJsonRequest("/api/admin/me", { token }).catch(() => handleLogout());
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const loadReferrals = async (view) => {
    setLoading(true);
    const v = view || referralView;
    try {
      const data = await safeJsonRequest(`/api/admin/referrals-by-view?view=${v}`, { token });
      setReferrals(data);
    } catch (err) {
      if (err.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const loadIntakes = async (filter, preference) => {
    const f = filter || intakeFilter;
    const p = preference !== undefined ? preference : preferenceFilter;
    setIntakesLoading(true);
    try {
      const base = f === "archived" ? "view=archived" : f !== "all" ? `status=${f}` : "";
      const prefParam = p ? `preference=${encodeURIComponent(p)}` : "";
      const params = [base, prefParam].filter(Boolean).join("&");
      const data = await safeJsonRequest(`/api/admin/intakes${params ? `?${params}` : ""}`, { token });
      setIntakes(data);
    } catch { /* silent */ }
    finally { setIntakesLoading(false); }
  };

  const handleExportIntakesCSV = async () => {
    setExportingIntakes(true);
    try {
      const base = intakeFilter === "archived" ? "view=archived" : intakeFilter !== "all" ? `status=${intakeFilter}` : "";
      const prefParam = preferenceFilter ? `preference=${encodeURIComponent(preferenceFilter)}` : "";
      const params = [base, prefParam].filter(Boolean).join("&");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/intakes/export${params ? `?${params}` : ""}`;
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const contentDisposition = xhr.getResponseHeader("Content-Disposition") || "";
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          const filename = match ? match[1] : "sdora_intakes.csv";
          const blob = new Blob([xhr.response], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }
        setExportingIntakes(false);
      };
      xhr.onerror = () => setExportingIntakes(false);
      xhr.send();
    } catch {
      setExportingIntakes(false);
    }
  };

  const loadDeclinedConsents = async () => {
    setDeclinedConsentsLoading(true);
    try {
      const data = await safeJsonRequest("/api/admin/intakes/declined-consents", { token });
      setDeclinedConsents(data);
    } catch { /* silent */ }
    finally { setDeclinedConsentsLoading(false); }
  };

  const handleToggleDeclinedConsents = () => {
    const next = !showDeclinedConsents;
    setShowDeclinedConsents(next);
    if (next) loadDeclinedConsents();
  };

  const handleExportDeclinedConsentsCSV = () => {
    setExportingDeclinedConsents(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/intakes/declined-consents/export`;
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const contentDisposition = xhr.getResponseHeader("Content-Disposition") || "";
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          const filename = match ? match[1] : "sdora_declined_consents.csv";
          const blob = new Blob([xhr.response], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }
        setExportingDeclinedConsents(false);
      };
      xhr.onerror = () => setExportingDeclinedConsents(false);
      xhr.send();
    } catch {
      setExportingDeclinedConsents(false);
    }
  };


  const loadAnalytics = async (days) => {
    const d = days || analyticsDays;
    setAnalyticsLoading(true);
    try {
      const data = await safeJsonRequest(`/api/analytics/summary?days=${d}`, { token });
      setAnalyticsData(data);
    } catch { /* silent */ }
    finally { setAnalyticsLoading(false); }
  };

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const data = await safeJsonRequest("/api/admin/audit-logs?limit=200", { token });
      setAuditLogs(data);
    } catch { /* silent */ } finally { setAuditLoading(false); }
  };

  const handleLogout = useCallback(() => {
    window.dispatchEvent(new Event("admin-logout"));
    safeJsonRequest("/api/admin/logout", { method: "POST", token }).catch(() => {});
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("admin_email");
    sessionStorage.removeItem("admin_role");
    sessionStorage.removeItem("admin_name");
    navigate("/admin");
  }, [navigate, token]);

  const handleStatusChange = (referralId, newStatus) => {
    setReferrals((prev) =>
      prev.map((r) => (r.id === referralId ? { ...r, status: newStatus } : r))
    );
  };

  const handleArchiveToggle = async (referralId, isArchived) => {
    const endpoint = isArchived ? "unarchive" : "archive";
    try {
      await safeJsonRequest(`/api/admin/referrals/${referralId}/${endpoint}`, { method: "POST", token });
      setReferrals((prev) => prev.filter((r) => r.id !== referralId));
    } catch { /* silent */ }
  };

  const switchReferralView = (view) => {
    setReferralView(view);
    setSelectedIds(new Set());
    setStatusFilter(null);
    loadReferrals(view);
  };

  // ── Bulk selection ──
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filteredReferrals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredReferrals.map((r) => r.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await safeJsonRequest("/api/admin/referrals/bulk-status", {
        method: "POST",
        body: { referral_ids: Array.from(selectedIds), status: newStatus },
        token,
      });
      setReferrals((prev) =>
        prev.map((r) => (selectedIds.has(r.id) ? { ...r, status: newStatus } : r))
      );
      setSelectedIds(new Set());
    } catch { /* silent */ } finally { setBulkLoading(false); }
  };

  const filteredReferrals = referrals.filter((r) => {
    // Status filter from analytics bar
    if (statusFilter && (r.status || "submitted") !== statusFilter) return false;
    // Text search
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (r.client_name_omhc || "").toLowerCase().includes(term) ||
      (r.client_name_prp || "").toLowerCase().includes(term) ||
      (r.referral_category || "").toLowerCase().includes(term) ||
      (r.id || "").toLowerCase().includes(term) ||
      (r.referral_source || "").toLowerCase().includes(term) ||
      (r.status || "").toLowerCase().includes(term)
    );
  });

  const allFilteredSelected = filteredReferrals.length > 0 && filteredReferrals.every((r) => selectedIds.has(r.id));

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Top Bar */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-lg font-bold">SDORA Admin Portal</h1>
            <p className="text-xs text-slate-400">HIPAA-Compliant Secure Dashboard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-200">{adminName}</p>
            <p className="text-xs text-slate-400">{adminEmail}</p>
          </div>
          <button onClick={() => setShowChangePassword(true)} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors" title="Change Password" data-testid="change-password-btn">
            <Key className="w-5 h-5 text-slate-300" />
          </button>
          <button onClick={handleLogout} className="p-2 rounded-lg bg-red-600/20 hover:bg-red-600/40 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("referrals")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "referrals" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
            data-testid="tab-referrals"
          >
            <FileText className="w-4 h-4 inline mr-1.5" />Referrals ({referrals.length})
          </button>
          <button
            onClick={() => { setActiveTab("audit"); if (auditLogs.length === 0) loadAuditLogs(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "audit" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
            data-testid="tab-audit"
          >
            <Lock className="w-4 h-4 inline mr-1.5" />Audit Logs
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "users" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4 inline mr-1.5" />Users
          </button>
          <button
            onClick={() => { setActiveTab("intakes"); setIntakeFilter("all"); loadIntakes("all"); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "intakes" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
            data-testid="tab-intakes"
          >
            <ClipboardCheck className="w-4 h-4 inline mr-1.5" />Intakes
          </button>
          <button
            onClick={() => { setActiveTab("analytics"); if (!analyticsData) loadAnalytics(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "analytics" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4 inline mr-1.5" />Page Views
          </button>
        </div>

        {/* Referral View Toggle (Active/Archived) — shown when on referrals tab */}
        {activeTab === "referrals" && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => switchReferralView("active")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${referralView === "active" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
              data-testid="view-active">
              Active
            </button>
            <button onClick={() => switchReferralView("archived")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${referralView === "archived" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"}`}
              data-testid="view-archived">
              <Archive className="w-3 h-3 inline mr-1" />Archived
            </button>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <>
            {/* Analytics */}
            {!loading && referrals.length > 0 && <AnalyticsBar referrals={referrals} activeFilter={statusFilter} onFilterChange={setStatusFilter} />}

            {/* Search */}
            <div className="mb-4 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, category, source, status..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="referral-search"
              />
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
              <div className="mb-4 px-4 py-3 bg-blue-900/30 border border-blue-700/50 rounded-xl flex items-center justify-between flex-wrap gap-2" data-testid="bulk-action-bar">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-blue-300 font-medium">{selectedIds.size} selected</span>
                  <button onClick={clearSelection} className="p-1 rounded hover:bg-blue-800/50" data-testid="bulk-clear">
                    <X className="w-3.5 h-3.5 text-blue-400" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-blue-400 mr-1">Set status:</span>
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleBulkStatus(opt.value)}
                      disabled={bulkLoading}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-slate-700 text-slate-300 border-slate-600 hover:border-blue-500 hover:text-white transition-all disabled:opacity-50`}
                      data-testid={`bulk-status-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                SSNs are encrypted at rest. Revealing requires password re-authentication. Auto-hide after 30 seconds. All actions are audit-logged.
              </p>
            </div>

            {/* Select All + List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <span className="w-8 h-8 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : filteredReferrals.length === 0 ? (
              <div className="text-center py-20 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{referrals.length === 0 ? "No referrals submitted yet" : "No referrals match your search"}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 hover:text-slate-300">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={selectAll}
                      className="w-3.5 h-3.5 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                      data-testid="select-all"
                    />
                    Select all ({filteredReferrals.length})
                  </label>
                </div>
                <div className="space-y-3">
                  {filteredReferrals.map((ref) => (
                    <ReferralCard
                      key={ref.id}
                      referral={ref}
                      token={token}
                      onStatusChange={handleStatusChange}
                      selected={selectedIds.has(ref.id)}
                      onSelectToggle={toggleSelect}
                      onArchive={handleArchiveToggle}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Audit Logs Tab */}
        {activeTab === "audit" && (
          <AuditLogPanel logs={auditLogs} loading={auditLoading} onRefresh={loadAuditLogs} token={token} />
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <UserManagementPanel token={token} currentEmail={adminEmail} currentRole={adminRole} />
        )}

        {/* Intakes Tab */}
        {activeTab === "intakes" && (
          <div>
            {/* Filter Tabs */}
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="intake-filter-tabs">
              {[
                { key: "all", label: "All Active" },
                { key: "new", label: "New" },
                { key: "in_review", label: "In Review" },
                { key: "completed", label: "Completed" },
                { key: "archived", label: "Archived" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setIntakeFilter(tab.key); loadIntakes(tab.key); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    intakeFilter === tab.key
                      ? "bg-blue-600 text-white"
                      : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                  }`}
                  data-testid={`intake-filter-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
              <div className="flex-1" />
              <select
                value={preferenceFilter}
                onChange={(e) => { setPreferenceFilter(e.target.value); loadIntakes(intakeFilter, e.target.value); }}
                className="bg-slate-800 border border-slate-700 rounded-lg text-xs text-white px-3 py-1.5"
                data-testid="intake-preference-filter"
              >
                <option value="">All Delivery Preferences</option>
                <option value="Onsite only">Onsite only</option>
                <option value="Offsite only">Offsite only</option>
                <option value="Blended">Blended</option>
              </select>
              <button onClick={() => loadIntakes()} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-300" data-testid="refresh-intakes">Refresh</button>
              <button
                onClick={handleExportIntakesCSV}
                disabled={exportingIntakes || intakes.length === 0}
                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-xs text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                data-testid="export-intakes-csv"
              >
                {exportingIntakes ? (
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Export CSV
              </button>
              <button
                onClick={handleToggleDeclinedConsents}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  showDeclinedConsents ? "bg-amber-600 text-white" : "bg-slate-800 text-amber-400 hover:bg-slate-700"
                }`}
                data-testid="toggle-declined-consents-report"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Declined Consents Report
              </button>
            </div>

            {showDeclinedConsents && (
              <div className="bg-slate-800 border border-amber-800/50 rounded-xl p-4 mb-4" data-testid="declined-consents-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-amber-400">Submissions With Declined Consent Items</h3>
                  <button
                    onClick={handleExportDeclinedConsentsCSV}
                    disabled={exportingDeclinedConsents || declinedConsents.length === 0}
                    className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-xs text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                    data-testid="export-declined-consents-csv"
                  >
                    {exportingDeclinedConsents ? (
                      <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Export CSV
                  </button>
                </div>
                {declinedConsentsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                  </div>
                ) : declinedConsents.length === 0 ? (
                  <p className="text-xs text-slate-500 py-4 text-center">No submissions with declined consent items found.</p>
                ) : (
                  <div className="space-y-2">
                    {declinedConsents.map((item) => (
                      <div key={item.id} className="bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 flex items-center justify-between" data-testid={`declined-consent-row-${item.id}`}>
                        <div>
                          <div className="text-sm font-medium text-white">{item.client_name}</div>
                          <div className="text-xs text-amber-400 mt-0.5">{item.declined_items.join(", ")}</div>
                        </div>
                        <button onClick={() => navigate(`/admin/intake/${item.id}`)} className="text-xs text-blue-400 hover:text-blue-300 font-medium" data-testid={`declined-consent-view-${item.id}`}>
                          View →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="text-xs text-slate-500 mb-3">{intakes.length} result{intakes.length !== 1 ? "s" : ""}</div>

            {intakesLoading ? (
              <div className="flex items-center justify-center py-20">
                <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : intakes.length === 0 ? (
              <p className="text-center py-20 text-sm text-slate-500">
                {intakeFilter === "all" ? "No intake submissions yet" : `No ${intakeFilter === "archived" ? "archived" : `"${intakeFilter}"`} intakes`}
              </p>
            ) : (
              <div className="space-y-2">
                {intakes.map((intake) => (
                  <div key={intake.id} className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-4 flex items-center justify-between" data-testid={`intake-card-${intake.id}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-white">{intake.client_name}</span>
                        {intake.safety_flag && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${intake.safety_flag_reviewed ? "text-green-400 bg-green-900/40" : "text-red-400 bg-red-900/40"}`} data-testid={`intake-safety-flag-${intake.id}`}>
                            {intake.safety_flag_reviewed ? "SAFETY FLAG · REVIEWED" : "SAFETY FLAG · UNREVIEWED"}
                          </span>
                        )}
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          intake.status === "new" ? "text-blue-400 bg-blue-900/40" :
                          intake.status === "in_review" ? "text-yellow-400 bg-yellow-900/40" :
                          intake.status === "completed" ? "text-green-400 bg-green-900/40" :
                          "text-slate-400 bg-slate-700"
                        }`}>{intake.status === "in_review" ? "In Review" : intake.status === "new" ? "New" : intake.status === "completed" ? "Completed" : intake.status}</span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          intake.source === "paper" ? "text-amber-400 bg-amber-900/30 border border-amber-800/40" : "text-teal-400 bg-teal-900/30 border border-teal-800/40"
                        }`} data-testid={`intake-source-${intake.id}`}>
                          {intake.source === "paper" ? "Paper" : "Online"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span>{intake.requested_service || (intake.source === "paper" ? "Paper Packet" : "N/A")}</span>
                        {intake.service_delivery_preference && (
                          <span className="text-indigo-400 bg-indigo-900/30 border border-indigo-800/40 rounded px-1.5 py-0.5" data-testid={`intake-preference-${intake.id}`}>
                            {intake.service_delivery_preference}
                          </span>
                        )}
                        <span>{intake.submitted_at ? new Date(intake.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Draft"}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/admin/intake/${intake.id}`)}
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium"
                      data-testid={`view-intake-${intake.id}`}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div data-testid="analytics-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-300 font-medium">Page Views</span>
                <div className="flex gap-1">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => { setAnalyticsDays(d); loadAnalytics(d); }}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        analyticsDays === d ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"
                      }`}
                      data-testid={`analytics-days-${d}`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => loadAnalytics()} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-300" data-testid="refresh-analytics">Refresh</button>
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-20">
                <span className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : !analyticsData ? (
              <p className="text-center py-20 text-sm text-slate-500">Loading analytics...</p>
            ) : (
              <>
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 mb-4" data-testid="analytics-summary">
                  <div className="text-3xl font-bold text-white mb-1">{analyticsData.total_views.toLocaleString()}</div>
                  <div className="text-xs text-slate-400">total page views in the last {analyticsData.period_days} days</div>
                </div>

                {analyticsData.pages.length === 0 ? (
                  <p className="text-center py-10 text-sm text-slate-500">No page views recorded yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {analyticsData.pages.map((page, i) => {
                      const maxViews = analyticsData.pages[0]?.views || 1;
                      const pct = Math.round((page.views / maxViews) * 100);
                      return (
                        <div key={page.path} className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`analytics-row-${i}`}>
                          <span className="text-xs text-slate-500 w-6 text-right">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white font-medium truncate">{page.path}</div>
                            <div className="w-full bg-slate-700 rounded-full h-1.5 mt-1">
                              <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <span className="text-sm font-bold text-blue-400 w-16 text-right">{page.views.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showChangePassword && (
        <ChangePasswordModal
          token={token}
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            setShowChangePassword(false);
            handleLogout();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
